import { CUSTOM_ELEMENTS_SCHEMA, Component, Inject } from '@angular/core';

import { CommunityTransactionForSaleQuery, CompanyParametersQuery, CurrencyCollectionQuery, CurrencyItem, UnitItem, UnitQuery, VatRateForPurchaseQuery, VatRateForSaleBaseQuery, VatRateForSaleQuery, VatRateForSaleWstoQuery, VatRateItem, sortVatRates, withModule } from '@skylab-shared/business';
import { IdentifiableValueConverter, LocalContext, ModelBase, RepositoryResponseCode, RootContextBase, buildValidationKey } from '@skylab-shared/core';
import { CalculationMethod, DocumentItemModel, EnumTranslatorBase, ExchangeRateModel, InventoryDocumentKind, LabeledEnumValue, LabeledEnumValueConverter, ProductKind, ProductModel, ProductOssVatRateModel, ProductPriceModel, PurchaseDocumentKind, PurchaseInvoiceDocumentItemModel, SaleDocumentItemModel, SaleDocumentKind } from '@skylab-shared/dataModel';

import { ProductBO, ProductRepository } from '@skylab-angular-shared/business';
import { EnumTranslator, L10n, L10nBase, RootContext } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogButtonResult, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, DialogState, FocusByDirective, FormContainerComponent, NumericValueAccessorDirective, SelectValueAccessorDirective, TextValueAccessorDirective, TextareaValueAccessorDirective, ToastService, TooltipDirective, ValidateDirective, ValidateInnerDirective, ValidationSourceDirective, IconComponent } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { ApplicationInsightsService } from '@skylab-angular-shared/config';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { RecursiveDiff } from '@skylab-shared/types';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { BehaviorSubject } from 'rxjs';
import { SuccessContext } from '../../form/form-state-handlers/form-state-success.service';
import { ViewValidation, ViewValidationService } from '../../form/form-validation';
import { UnitDialogComponent, UnitDialogResult } from '../unit/unit-dialog.component';

type PriceKind = 'sale' | 'purchase';

interface DocumentModelPart extends ModelBase {
    readonly kind: SaleDocumentKind | InventoryDocumentKind | PurchaseDocumentKind;
    readonly exchangeRate: ExchangeRateModel | null;
    readonly currencyId: number;
    readonly calculationMethod: CalculationMethod;
}

interface DocumentItemModelPart extends ModelBase {
    unitId: number;
    productName: string | null;
    symbol: string;
    pkwiu: string | null;
    cnCode: string | null;
    price_netBeforeDiscount: number;
    price_grossBeforeDiscount: number;
    jpkVatGroups: string[];
    vatRateId: number;
    barCode: string;
}

interface ProductDialogViewModel extends DialogContentViewModel {
    isDirty: boolean;
    isSaving: boolean;
    isFormReady: boolean;
    showValidations: BehaviorSubject<boolean>,
    bo: ProductBO;

    units: UnitItem[];
    unitValueConverter: IdentifiableValueConverter;

    vatRates: VatRateItem[];
    vatRateValueConverter: IdentifiableValueConverter;

    purchaseVatRates: VatRateItem[];
    purchaseVatRateValueConverter: IdentifiableValueConverter;

    baseCurrency: CurrencyItem;

    priceKind: PriceKind;

    productKinds: LabeledEnumValue[];
    productKindValueConverter: LabeledEnumValueConverter<ProductKind>;

    inventorySectionActive: () => boolean;
    isInventoryDocument: boolean;
    isInventoryControlDisabled: boolean;

    salePrice: {
        price: ProductPriceModel;
        symbol: string;
        precision: number;
    };
}

export interface ProductDialogData {
    readonly documentItemData: RecursiveDiff<DocumentItemModelPart, ModelBase>;
    readonly document: RecursiveDiff<DocumentModelPart, ModelBase>;
    readonly inventoryDisabled?: boolean;
}

export interface ProductDialogResult {
    id: number;
}

@Component({
    selector: 'skl-product-dialog',
    templateUrl: './product-dialog.component.html',
    styles: [`
        .inventory-control {
            margin: var(--gutter-medium) 0;
        }
    `],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, SelectValueAccessorDirective, FormsModule, BooleanValueAccessorDirective, ValidateDirective, TooltipDirective, TextValueAccessorDirective, FocusByDirective, TextareaValueAccessorDirective, ValidateInnerDirective, NumericValueAccessorDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class ProductDialogComponent extends DialogContentComponentBase<ProductDialogViewModel, ProductDialogData> {
    private bo!: ProductBO;
    private localContext: LocalContext;
    public viewValidation!: ViewValidation;

    public constructor(
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase,
        @Inject(ToastService) private readonly toastService: ToastService,
        @Inject(L10n) private readonly l10n: L10nBase,
        private readonly productRepository: ProductRepository,
        private readonly viewValidationService: ViewValidationService,
        @Inject(RootContext) private readonly rootContext: RootContextBase,
        private readonly insights: ApplicationInsightsService,

        dialogService: DialogInsService
    ) {
        super(dialogService);
        this.localContext = this.rootContext.createLocalContext();
    }

    protected override getInitialState(): ProductDialogViewModel {
        return {
            ...super.getInitialState(),
            isDirty: false,
            showValidations: new BehaviorSubject(false),
            isSaving: false
        };
    }

    protected async preloadDataAsync(): Promise<void> {
        await Promise.all([
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(CommunityTransactionForSaleQuery),
            this.localContext.preload(UnitQuery),
            this.localContext.preload(VatRateForSaleBaseQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(VatRateForSaleWstoQuery),
            this.localContext.preload(VatRateForSaleQuery)
        ]);
    }

    private async prepareBOAsync(): Promise<void> {
        this.bo = await this.productRepository.createNewAsync(this.localContext);

        if(this.data.documentItemData.symbol){
            this.bo.model.symbol = this.data.documentItemData.symbol;
        }
        this.bo.model.kind = ProductKind.Merchandize;
        this.bo.model.name = this.data.documentItemData.productName || '';
        this.bo.model.unitId = this.data.documentItemData.unitId;
        this.bo.model.pkwiu = this.data.documentItemData.pkwiu;
        this.bo.model.cnCode = this.data.documentItemData.cnCode;
        this.bo.model.barCode = this.data.documentItemData.barCode;
        this.bo.model.jpkVatGroups = this.data.documentItemData.jpkVatGroups;
        this.bo.model.inventoryControl = withModule('Inventory') && (this.data.document.kind in InventoryDocumentKind);

        this.setPrices();
    }

    protected override async prepareDataAsync(): Promise<void> {
        const self = this;

        await this.preloadDataAsync();
        await this.prepareBOAsync();
        this.viewModel.bo = this.bo;

        const reverseChargeCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { reverseCharge: true });
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery);

        this.viewModel.units = this.localContext.getPreloaded(UnitQuery) || [];
        this.viewModel.unitValueConverter = new IdentifiableValueConverter(this.viewModel.units);

        this.viewModel.vatRates = sortVatRates((this.localContext.getPreloaded(VatRateForSaleBaseQuery) || [])
            .filter(vr => !vr.communityTransactions.some(id => id === reverseChargeCommunityTransaction?.id)));
        this.viewModel.vatRateValueConverter = new IdentifiableValueConverter(this.viewModel.vatRates);

        this.viewModel.purchaseVatRates = sortVatRates(this.localContext.getPreloaded(VatRateForPurchaseQuery) || []);
        this.viewModel.purchaseVatRateValueConverter = new IdentifiableValueConverter(this.viewModel.purchaseVatRates);

        this.viewModel.productKinds = this.enumTranslator.getLabeledEnumValueArray(ProductKind);
        this.viewModel.productKindValueConverter = new LabeledEnumValueConverter(this.enumTranslator, ProductKind);

        const systemCurrency = companyParameters?.systemCurrency;
        const baseCurrency = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }) : undefined;
        if (!baseCurrency) {
            throw new Error('Base currency not found');
        }
        this.viewModel.baseCurrency = baseCurrency;

        this.viewValidation = this.viewValidationService.create([this.viewModel.bo], this.viewModel.showValidations);

        this.viewModel.inventorySectionActive = () => withModule('Inventory') && (this.bo.model.kind === ProductKind.Merchandize);
        this.viewModel.isInventoryDocument = this.data.document.kind in InventoryDocumentKind;
        this.viewModel.isInventoryControlDisabled = !!(this.data.inventoryDisabled || this.viewModel.isInventoryDocument);

        this.viewModel.priceKind = this.priceKind;

        const salePrice = this.bo.model.prices.find(elem => elem.currencyId === this.data.document.currencyId);
        const currencies = this.localContext.getPreloaded(CurrencyCollectionQuery) || [];
        const docCurrency = currencies.find(elem => elem.id === self.data.document.currencyId);

        this.viewModel.salePrice = {
            get price(): ProductPriceModel {
                return salePrice || new ProductPriceModel();
            },
            symbol: docCurrency?.symbol || '',
            precision: docCurrency?.precision ?? 2
        };
    }

    private get priceKind(): PriceKind {
        if ((this.data.document.kind in PurchaseDocumentKind)
            || [InventoryDocumentKind.GoodsReceipt, InventoryDocumentKind.GoodsReceiptAdjustment, InventoryDocumentKind.InternalGoodsReceipt].includes(this.data.document.kind as InventoryDocumentKind)) {
            return 'purchase';
        } else {
            return 'sale';
        }
    }

    private setPrices(): void {
        const saleVatRates = this.localContext.getPreloaded(VatRateForSaleQuery) || [];
        const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        const systemCurrencyId = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id : undefined;
        if (systemCurrencyId === undefined) {
            throw new Error('System currency ID not found');
        }
        const purchaseVatRates = this.localContext.getPreloaded(VatRateForPurchaseQuery) || [];

        const salePrice = new ProductPriceModel();
        this.bo.model.prices.push(salePrice);
        salePrice.currencyId = systemCurrencyId;

        this.bo.model.purchasePrice = new ProductPriceModel();
        this.bo.model.purchasePrice.currencyId = systemCurrencyId;

        if (this.priceKind === 'purchase') {
            const defRate = saleVatRates.find(elem => elem.default);
            if (defRate) {
                this.bo.model.vatRateId = defRate.id;
            }

            this.setPurchaseVatRate();
            this.setPurchasePrices();
        } else {
            const defRate = purchaseVatRates.find(vatRate => vatRate.default);
            if (defRate) {
                this.bo.model.purchaseVatRateId = defRate.id;
            }

            this.setSaleVatRate();
            this.setSalePrices();
        }
    }

    private setPurchasePrices(): void {
        const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        const systemCurrencyId = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id : undefined;
        if (systemCurrencyId === undefined) {
            throw new Error('System currency ID not found');
        }
        this.bo.model.purchaseVatRateId = this.data.documentItemData.vatRateId;

        let netBaseCurrency = this.data.documentItemData.price_netBeforeDiscount;
        let grossBaseCurrency = this.data.documentItemData.price_grossBeforeDiscount;

        if (this.isCurrencyDocument) {
            const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: systemCurrencyId })!;
            netBaseCurrency = Calc.round(this.data.documentItemData.price_netBeforeDiscount * this.exchangeRateCalculationMultiplier, precision, MidpointRounding.AwayFromZero);
            grossBaseCurrency = Calc.round(this.data.documentItemData.price_grossBeforeDiscount * this.exchangeRateCalculationMultiplier, precision, MidpointRounding.AwayFromZero);
        }

        if ([CalculationMethod.Net, CalculationMethod.Manual].includes(this.data.document.calculationMethod)) {
            this.bo.model.purchasePrice.net = netBaseCurrency;
        } else if (this.data.document.calculationMethod === CalculationMethod.Gross) {
            this.bo.model.purchasePrice.gross = grossBaseCurrency;
        }
    }

    private setPurchaseVatRate(): void {
        this.bo.model.purchaseVatRateId = this.data.documentItemData.vatRateId;
    }

    private get exchangeRateCalculationMultiplier(): number {
        const rate = this.data.document.exchangeRate?.rate || 1;
        const units = this.data.document.exchangeRate?.units || 1;

        return rate / units;
    }

    private recalculateBaseCurrencySalePrice(): void {
        const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        const systemCurrencyId = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id : undefined;
        if (systemCurrencyId === undefined) {
            throw new Error('System currency ID not found');
        }

        const currencyPrice = this.bo.model.prices.find(elem => elem.currencyId === this.data.document.currencyId);
        if (!currencyPrice) {
            return;
        }

        const precision = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: systemCurrencyId })?.precision ?? 2;
        const netBaseCurrency = Calc.round(currencyPrice.net * this.exchangeRateCalculationMultiplier, precision, MidpointRounding.AwayFromZero);
        const grossBaseCurrency = Calc.round(currencyPrice.gross * this.exchangeRateCalculationMultiplier, precision, MidpointRounding.AwayFromZero);

        let basePrice = this.bo.model.prices.find(elem => elem.currencyId === systemCurrencyId);
        if (!basePrice) {
            basePrice = new ProductPriceModel();
            this.bo.model.prices.push(basePrice);
        }
        this.setPrice(basePrice, { net: netBaseCurrency, gross: grossBaseCurrency }, systemCurrencyId);
    }

    private setSalePrices(): void {
        const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        const systemCurrencyId = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id : undefined;
        if (systemCurrencyId === undefined) {
            throw new Error('System currency ID not found');
        }

        if (this.isCurrencyDocument) {
            const priceCurrency = new ProductPriceModel();
            this.bo.model.prices.push(priceCurrency);
            this.setPrice(priceCurrency, { net: this.data.documentItemData.price_netBeforeDiscount, gross: this.data.documentItemData.price_grossBeforeDiscount }, this.data.document.currencyId);

            this.recalculateBaseCurrencySalePrice();
        } else {
            let basePrice = this.bo.model.prices.find(elem => elem.currencyId === systemCurrencyId);
            if (!basePrice) {
                basePrice = new ProductPriceModel();
                this.bo.model.prices.push(basePrice);
            }
            this.setPrice(basePrice, { net: this.data.documentItemData.price_netBeforeDiscount, gross: this.data.documentItemData.price_grossBeforeDiscount }, systemCurrencyId);
        }

    }

    private get isCurrencyDocument(): boolean {
        const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        const systemCurrencyId = systemCurrency ? this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })?.id : undefined;
        return systemCurrencyId !== this.data.document.currencyId;
    }

    private setSaleVatRate(): void {
        const newVatRateId = this.data.documentItemData.vatRateId;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery) || [];

        if (!newVatRateId) {
            const defRate = vatRates.find(e => e.default);
            if (defRate) {
                this.bo.model.vatRateId = defRate.id;
            }
            return;
        }

        const ossVatRates = this.localContext.getPreloaded(VatRateForSaleWstoQuery) || [];

        const ossVatRate = ossVatRates.find(elem => elem.id === newVatRateId);
        if (ossVatRate) {
            const defRate = vatRates.find(vatRate => vatRate.default);
            if (defRate) {
                this.bo.model.vatRateId = defRate.id;
            }

            const productOssVatRate = new ProductOssVatRateModel();
            this.bo.model.ossVatRates.push(productOssVatRate);
            productOssVatRate.countryId = ossVatRate.countryId;
            productOssVatRate.vatRateId = ossVatRate.id;
        } else {
            this.bo.model.vatRateId = newVatRateId;
        }
    }

    private setPrice(price: ProductPriceModel, itemPrice: { net: number, gross: number }, currencyId: number): void {
        price.currencyId = currencyId;

        if (this.data.document.calculationMethod === CalculationMethod.Net) {
            price.net = itemPrice.net;
        } else if (this.data.document.calculationMethod === CalculationMethod.Gross || this.data.document.calculationMethod === CalculationMethod.Margin) {
            price.gross = itemPrice.gross;
        }
    }

    protected override isReady() {
        this.viewModel.isFormReady = true;
        this.insights.trackEvent('FE_SAVE_ONE_USAGE_PRODUCT_DIALOG_OPENED');
    }

    protected getSuccessContext(): SuccessContext {
        return {
            message: this.bo.model.kind === ProductKind.Merchandize
                ? this.l10n._T('scenes.products.form.form-state-context.success.message-merchandize', { name: this.bo.model.name })
                : this.l10n._T('scenes.products.form.form-state-context.success.message-service', { name: this.bo.model.name })
        };
    }

    public async saveAsync(): Promise<void> {
        try {
            if (this.isCurrencyDocument && (this.priceKind === 'sale')) this.recalculateBaseCurrencySalePrice();

            this.viewModel.isSaving = true;
            this.viewModel.showValidations.next(true);
            await this.bo.waitForProcessingCompletionAsync();

            if (!this.bo.isValid) return;

            const response = await this.productRepository.saveAsync(this.bo);
            if (response.code !== RepositoryResponseCode.Success) {

                this.toastService.showAsync({
                    text: this.l10n._T('scenes.shared.dialog.product.save-error'),
                    type: 'error'
                });

                return;
            };

            this.toastService.showAsync({
                text: this.getSuccessContext().message,
                type: 'success'
            });

            this.dialogService.close({
                state: DialogState.Confirm,
                data: {
                    id: response.object?.id ?? 0
                }
            });
        } catch (err) {
            this.dialogService.close({ state: DialogState.Cancel });
            this.logger.error(err);
        } finally {
            this.viewModel.isSaving = false;
        }
    }

    public getValidationKey(propertyName: string): string | undefined {
        if (this.viewModel.bo && this.viewModel.bo.model)
            return buildValidationKey(this.viewModel.bo.model.sun, propertyName);
        return undefined;
    }

    public async addUnitAsync(): Promise<void> {
        try {
            const newUnit = await this.dialogService.showAsync<void, UnitDialogResult>({ component: UnitDialogComponent });
            if (newUnit.state === DialogState.Confirm) {
                const newUnits = [...(await this.localContext.refresh(UnitQuery))];
                this.viewModel.units = this.viewModel.unitValueConverter.items = newUnits;
                if (newUnit.data) {
                    this.bo.model.unitId = newUnit.data.id;
                }
            }

        } catch (err) {
            this.logger.error(err);
        }
    }


    protected override dispose(): void {
        super.dispose();
        this.deregisterAllListeners();
        this.viewValidation?.dispose();
        this.bo?.dispose();
        this.localContext?.dispose();
    }

    public override async onAction(data: DialogButtonResult): Promise<void> {
        if (data.state === DialogState.Confirm) {
            await this.saveAsync();
        } else {
            this.onCancel();
        }
    }
}

export function updateDocumentItemFromProductDialogData(item: DocumentItemModel, product: ProductModel, ctx: { vatPayer?: boolean, availableVatRates: VatRateItem[], kind: 'sale' | 'purchase' }): void {
    item.symbol = product.symbol;
    item.productId = product.id;
    item.unitId = product.unitId;
    item.productName = product.name;

    const vatRateId = ctx.kind === 'sale' ? product.vatRateId : product.purchaseVatRateId;

    if (ctx.vatPayer === false) {
        const exempt = ctx.availableVatRates.find(elem => elem.exempt);
        if (exempt) {
            item.vatRateId = exempt.id;
        }
    } else if (ctx.availableVatRates.find(elem => elem.id === vatRateId)) {
        item.vatRateId = vatRateId;
    }
}


export function updateDocumentItemWithInventoryControlFromProductDialogData(item: SaleDocumentItemModel | PurchaseInvoiceDocumentItemModel, product: ProductModel, ctx: { vatPayer?: boolean, availableVatRates: VatRateItem[], kind: 'sale' | 'purchase' }): void {
    updateDocumentItemFromProductDialogData(item, product, ctx);
    item.includesInventoryControl = product.inventoryControl;
}
